import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { ApiKeyItem, CreateApiKeyInput } from './types';

const INITIAL_FALLBACK_KEYS: ApiKeyItem[] = [
  {
    id: 'key-1',
    name: 'Production Backend Integration',
    key_prefix: 'hl_live_prod',
    masked_key: 'hl_live_prod_••••••••a4f1',
    scopes: ['core.read', 'core.write', 'contacts.manage'],
    created_at: new Date(Date.now() - 14 * 86400000).toISOString(),
    last_used_at: new Date(Date.now() - 12 * 60000).toISOString(),
    status: 'active',
  },
  {
    id: 'key-2',
    name: 'CI/CD Pipeline Service Account',
    key_prefix: 'hl_live_cicd',
    masked_key: 'hl_live_cicd_••••••••88bc',
    scopes: ['core.read', 'custom_fields.read'],
    created_at: new Date(Date.now() - 45 * 86400000).toISOString(),
    last_used_at: new Date(Date.now() - 2 * 3600000).toISOString(),
    status: 'active',
  },
];

export function useApiKeys() {
  const [keys, setKeys] = useState<ApiKeyItem[]>(INITIAL_FALLBACK_KEYS);
  const [isLoading, setIsLoading] = useState(true);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);

  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await coreApi.get<ApiKeyItem[]>('/api/keys');
      if (Array.isArray(data)) {
        setKeys(data);
      } else {
        setKeys(INITIAL_FALLBACK_KEYS);
      }
    } catch {
      setKeys(INITIAL_FALLBACK_KEYS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const createKey = async (input: CreateApiKeyInput) => {
    try {
      const res = await coreApi.post<ApiKeyItem>('/api/keys', input);
      if (res && res.raw_key) {
        setNewlyCreatedKey(res.raw_key);
        setKeys((prev) => [res, ...prev]);
        return res;
      }
    } catch {
      // client-side fallback
    }
    const mockRaw = `hl_live_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    const mockKey: ApiKeyItem = {
      id: `key-${Date.now()}`,
      name: input.name,
      key_prefix: 'hl_live',
      masked_key: `hl_live_••••••••${mockRaw.slice(-4)}`,
      raw_key: mockRaw,
      scopes: input.scopes.length > 0 ? input.scopes : ['core.read'],
      created_at: new Date().toISOString(),
      status: 'active',
    };
    setNewlyCreatedKey(mockRaw);
    setKeys((prev) => [mockKey, ...prev]);
    return mockKey;
  };

  const revokeKey = async (id: string) => {
    try {
      await coreApi.delete(`/api/keys/${id}`);
    } catch {
      // fallback
    }
    setKeys((prev) =>
      prev.map((k) => (k.id === id ? { ...k, status: 'revoked' as const } : k))
    );
  };

  return {
    keys,
    isLoading,
    newlyCreatedKey,
    clearNewKey: () => setNewlyCreatedKey(null),
    createKey,
    revokeKey,
    refresh: fetchKeys,
  };
}

export default useApiKeys;
